import { window } from "global";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { getUrl } from "@outplayed/riot-assets";
import { useQuery } from "@apollo/client";
import { directToPage } from "lib/redirect-helpers";
import { parseQueryString } from "lib/general-helper.js";
import PerformanceAnalysisHeader from "./PerformanceAnalysisHeader";
import PerformanceAnalysisYourStats from "./PerformanceAnalysisYourStats";
import PerformanceAnalysisMultiKills from "./PerformanceAnalysisMultiKills";
import PerformanceAnalysisAllTheDamage from "./PerformanceAnalysisAllTheDamage";
import PerformanceAnalysisBestAndWorstCarousel from "./PerformanceAnalysisBestAndWorstCarousel";
import PerformanceAnalysisChampionBreakdown from "./PerformanceAnalysisChampionBreakdown";
import PerformanceAnalysisMostPlayedChamps from "./PerformanceAnalysisMostPlayedChamps";
import PerformanceAnalysisPositionBreakdown from "./PerformanceAnalysisPositionBreakdown";
import PerformanceAnalysisTeamPlayers from "./PerformanceAnalysisTeamPlayers";
import YEAR_IN_REVIEW from "components/Pages/PerformanceReview/queries/yearInReviewQuery";
import PerformanceAnalysisFooter from "./PerformanceAnalysisFooter";
import PerformanceAnalysisNotFound from "./PerformanceAnalysisNotFound";
import CHECK_YIR_STATUS from "components/Pages/PerformanceReview/queries/checkYearInReviewStatus";
import { GET_USER_SETTINGS } from "api/graphql/queries";
import PerformanceAnalysisLoading from "./PerformanceAnalysisLoading";

const processingStatuses = ["processing", "enqueued"];

const PerformanceAnalysisContainer = (props) => {
  const [init, setInit] = useState(false);
  const history = useHistory();
  const directToYIRLanding = directToPage("yirLanding");

  const parsedSearch = parseQueryString(history.location.search.substring(1));
  const { region, summonerName, year, sharing } = parsedSearch;
  const { data: userSettingsData, loading: settingsLoading } = useQuery(GET_USER_SETTINGS, {
    skip: sharing,
    fetchPolicy: "network-only",
  });

  let queryVars = null;

  if (sharing) {
    queryVars = {
      regionId: region,
      summonerName: summonerName,
      season: 14,
    };
  } else if (userSettingsData) {
    queryVars = {
      regionId: userSettingsData.getSettings.lolSettings.summoners[0].regionId,
      summonerName: userSettingsData.getSettings.lolSettings.summoners[0].summonerName,
      season: 14,
    };
  }

  const { data: yirStatus, loading: isYIRStatusCheckLoading } = useQuery(CHECK_YIR_STATUS, {
    variables: queryVars,
    fetchPolicy: "network-only",
    skip: !userSettingsData && !sharing,
  });

  const isYIRStatusReady = yirStatus && yirStatus.summonerReviewStatus.status === "ready";
  const isYIRStatusProcessing = yirStatus && processingStatuses.includes(yirStatus.summonerReviewStatus.status);

  useEffect(() => {
    window && setInit(true);
  });

  const { data: yirData } = useQuery(YEAR_IN_REVIEW, {
    variables: queryVars,
    skip: !isYIRStatusReady,
  });

  // check if finished loading all queries
  if (!isYIRStatusCheckLoading && !settingsLoading) {
    if (!isYIRStatusReady && !isYIRStatusProcessing) {
      if (sharing) {
        return (
          <div className="performance-container">
            <PerformanceAnalysisNotFound />
          </div>
        );
      } else {
        directToYIRLanding();
      }
    } else if (isYIRStatusProcessing) {
      return (
        <div className="performance-container">
          <PerformanceAnalysisLoading />
        </div>
      );
    }
  }

  if (!init || !yirData || !yirStatus) {
    return <div />;
  }

  const {
    summonersRiftStats,
    howlingAbyssStats,
    multiKills,
    allTheDamage,
    BestAndWorst,
    teamPlayers,
    playerInfo,
    soloRankScore,
    flexRankScore,
    mostPlayedChamps,
    positionBreakdown,
    summonersRiftTopThree,
    howlingAbyssTopThree,
  } = yirData.endOfYearReview;

  let summonerStats = [];
  if (teamPlayers.bestWinRate.accountId !== "-1")
    summonerStats.push({
      iconID: teamPlayers.bestWinRate.profileIcon,
      statText: `${(teamPlayers.bestWinRate.winRate * 100).toFixed(0)}%`,
      label: "BEST WINRATE",
      summonerName: teamPlayers.bestWinRate.summonerName,
    });
  if (teamPlayers.mostAssists.accountId !== "-1")
    summonerStats.push({
      iconID: teamPlayers.mostAssists.profileIcon,
      statText: teamPlayers.mostAssists.assists,
      label: "MOST ASSISTS",
      summonerName: teamPlayers.mostAssists.summonerName,
    });
  if (teamPlayers.bestKda.accountId !== "-1")
    summonerStats.push({
      iconID: teamPlayers.bestKda.profileIcon,
      statText: teamPlayers.bestKda.kda.toFixed(1),
      label: "BEST KDA",
      summonerName: teamPlayers.bestKda.summonerName,
    });

  return !summonersRiftStats && !howlingAbyssStats ? (
    <div className="performance-container">
      <div className="large-header">No games played in 2020!</div>
    </div>
  ) : (
    <div className="performance-container">
      <PerformanceAnalysisHeader
        summonerName={queryVars ? queryVars.summonerName : "SUMMONER NAME"}
        playerInfo={playerInfo}
        soloRankScore={soloRankScore}
        flexRankScore={flexRankScore}
        mostPlayedChamps={mostPlayedChamps}
        howlingAbyssTopThree={howlingAbyssTopThree}
      />
      <PerformanceAnalysisYourStats summonerRiftStats={summonersRiftStats} howlingAbyssStats={howlingAbyssStats} />
      {(summonersRiftStats || howlingAbyssStats) && (
        <img
          className="img-divider mini"
          src={getUrl("lol/performance_analysis/images/performance_analysis_mini_divider.png")}
        ></img>
      )}
      <PerformanceAnalysisMultiKills multiKills={multiKills} />
      {multiKills && (
        <img className="img-divider" src={getUrl("lol/performance_analysis/images/performance_analysis_divider.png")}></img>
      )}
      <PerformanceAnalysisChampionBreakdown
        summonersRiftTopThree={summonersRiftTopThree}
        howlingAbyssTopThree={howlingAbyssTopThree}
      />
      {(summonersRiftTopThree || howlingAbyssTopThree) && (
        <img
          className="img-divider mini"
          src={getUrl("lol/performance_analysis/images/performance_analysis_mini_divider.png")}
        ></img>
      )}
      <PerformanceAnalysisAllTheDamage allTheDamage={allTheDamage} />
      {allTheDamage && (
        <img
          className="img-divider mini"
          src={getUrl("lol/performance_analysis/images/performance_analysis_mini_divider.png")}
        ></img>
      )}
      <PerformanceAnalysisBestAndWorstCarousel bestAndWorstData={BestAndWorst} />
      {BestAndWorst && (
        <img className="img-divider" src={getUrl("lol/performance_analysis/images/performance_analysis_divider.png")}></img>
      )}
      {positionBreakdown && positionBreakdown.length > 0 && (
        <PerformanceAnalysisPositionBreakdown positionBreakdown={positionBreakdown} />
      )}
      {positionBreakdown && positionBreakdown.length > 0 && (
        <img
          className="img-divider mini"
          src={getUrl("lol/performance_analysis/images/performance_analysis_mini_divider.png")}
        ></img>
      )}
      {mostPlayedChamps && mostPlayedChamps.length > 0 && (
        <PerformanceAnalysisMostPlayedChamps mostPlayedChamps={mostPlayedChamps} />
      )}
      {mostPlayedChamps && mostPlayedChamps.length > 0 && (
        <img className="img-divider" src={getUrl("lol/performance_analysis/images/performance_analysis_divider.png")}></img>
      )}
      {summonerStats.length === 3 && <PerformanceAnalysisTeamPlayers teamPlayers={teamPlayers} />}
      {summonerStats.length === 3 && (
        <img className="img-divider" src={getUrl("lol/performance_analysis/images/performance_analysis_divider.png")}></img>
      )}
      <div className="performance-analysis_footer-container">
        <PerformanceAnalysisFooter />
      </div>
    </div>
  );
};

export default PerformanceAnalysisContainer;
